
import { PrintFilters } from "./types";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export function PrintsFilters({
  filters,
  onFiltersChange,
}: {
  filters: PrintFilters;
  onFiltersChange: (filters: PrintFilters) => void;
}) {
  const handleStatusChange = (value: string) => {
    onFiltersChange({
      ...filters,
      status: value,
    });
  };

  const handleCertifiedChange = (value: string) => {
    onFiltersChange({
      ...filters,
      certified: value,
    });
  };

  const handleProviderChange = (value: string) => {
    onFiltersChange({
      ...filters,
      provider_id: value,
    });
  };

  const handleCreatorChange = (value: string) => {
    onFiltersChange({
      ...filters,
      creator_id: value,
    });
  };

  const handlePrintTypeChange = (value: string) => {
    onFiltersChange({
      ...filters,
      print_type: value,
    });
  };

  // Fetch providers for the select
  const { data: providers } = useQuery({
    queryKey: ["providers"],
    queryFn: async () => {
      console.log("Fetching providers");
      const { data, error } = await supabase
        .from("print_service_providers")
        .select("provider_id, name")
        .eq("is_active", true)
        .order("name");

      if (error) {
        console.error("Error fetching providers:", error);
        throw error;
      }

      console.log("Fetched providers:", data);
      return data;
    },
  });

  // Fetch creators for the select
  const { data: creators } = useQuery({
    queryKey: ["creators"],
    queryFn: async () => {
      console.log("Fetching creators");
      const { data, error } = await supabase
        .from("creators")
        .select("creator_id, name, surname, nick_name")
        .order("surname");

      if (error) {
        console.error("Error fetching creators:", error);
        throw error;
      }

      console.log("Fetched creators:", data);
      return data;
    },
  });

  return (
    <div className="mb-4 flex gap-4 flex-wrap">
      <div className="w-[200px]">
        <Select value={filters.status} onValueChange={handleStatusChange}>
          <SelectTrigger>
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="waiting_to_print">Waiting to Print</SelectItem>
            <SelectItem value="printed">Printed</SelectItem>
            <SelectItem value="shipped">Shipped</SelectItem>
            <SelectItem value="in_cart">In Cart</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="w-[200px]">
        <Select value={filters.certified} onValueChange={handleCertifiedChange}>
          <SelectTrigger>
            <SelectValue placeholder="Filter by certification" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Prints</SelectItem>
            <SelectItem value="certified">Certified Only</SelectItem>
            <SelectItem value="not_certified">Not Certified</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="w-[200px]">
        <Select value={filters.provider_id} onValueChange={handleProviderChange}>
          <SelectTrigger>
            <SelectValue placeholder="Filter by provider" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Providers</SelectItem>
            {providers?.map((provider) => (
              <SelectItem key={provider.provider_id} value={provider.provider_id.toString()}>
                {provider.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="w-[200px]">
        <Select value={filters.creator_id} onValueChange={handleCreatorChange}>
          <SelectTrigger>
            <SelectValue placeholder="Filter by creator" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Creators</SelectItem>
            {creators?.map((creator) => (
              <SelectItem key={creator.creator_id} value={creator.creator_id.toString()}>
                {creator.nick_name 
                  ? `${creator.nick_name} / ${creator.name} ${creator.surname}`
                  : `${creator.name} ${creator.surname}`}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="w-[200px]">
        <Select value={filters.print_type} onValueChange={handlePrintTypeChange}>
          <SelectTrigger>
            <SelectValue placeholder="Filter by print type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Print Types</SelectItem>
            <SelectItem value="standard">Standard Prints</SelectItem>
            <SelectItem value="test">Test Prints</SelectItem>
            <SelectItem value="sample">Sample Prints</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}
